import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ArrowUpRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { apiErrorMessage } from '../../lib/apiError';

export default function PartnerSignup() {
  const nav = useNavigate();
  const { partner, signup, loading } = usePartnerAuth();
  const [form, setForm] = useState({
    name: '', email: '', password: '', company: '', phone: '', country: '',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && partner) nav('/partner', { replace: true });
  }, [partner, loading, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password || !form.company) {
      return toast.error('Name, email, password and company are required');
    }
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters');
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.phone) delete payload.phone;
      if (!payload.country) delete payload.country;
      await signup(payload);
      toast.success('Application received. An admin will approve shortly.');
      nav('/partner', { replace: true });
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F1E8] flex items-center justify-center p-6">
      <div className="w-full max-w-[520px]">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-full bg-[#0B3B2E] flex items-center justify-center">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M4 20 L12 4 L20 20" stroke="#C8944B" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="font-serif text-[22px] font-semibold text-[#0B3B2E]">
            Vector<span className="text-[#C8944B]">FX</span>
          </span>
        </Link>

        <div className="bg-white rounded-2xl border border-[#0B3B2E]/10 shadow-xl p-8">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#0B3B2E]/70 font-medium">Referral partners</p>
          <h1 className="mt-2 font-serif text-[30px] leading-tight text-[#0B3B2E]">Become a Vector FX partner</h1>
          <p className="mt-2 text-[13px] text-[#0E1F1A]/60">
            Refer clients and earn recurring revenue. Applications are approved manually within one business day.
          </p>

          <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Row label="Full name"><Input value={form.name} onChange={(e) => set('name', e.target.value)} /></Row>
            <Row label="Work email"><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></Row>
            <Row label="Company" wide><Input value={form.company} onChange={(e) => set('company', e.target.value)} /></Row>
            <Row label="Phone (optional)"><Input value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Row>
            <Row label="Country (optional)"><Input value={form.country} onChange={(e) => set('country', e.target.value)} /></Row>
            <Row label="Password" wide>
              <Input type="password" value={form.password} onChange={(e) => set('password', e.target.value)} placeholder="Minimum 6 characters" />
            </Row>
            <div className="sm:col-span-2 pt-2">
              <Button type="submit" disabled={busy}
                className="w-full rounded-full h-11 bg-[#0B3B2E] text-[#F5F1E8] hover:bg-[#12503F] font-medium disabled:opacity-70">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating account</>
                  : <>Submit application <ArrowUpRight className="w-4 h-4 ml-1" /></>}
              </Button>
            </div>
          </form>

          <p className="mt-6 text-center text-[13px] text-[#0E1F1A]/60">
            Already a partner? <Link to="/partner/login" className="underline text-[#0B3B2E] font-medium">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Row({ label, wide, children }) {
  return (
    <div className={`space-y-1 ${wide ? 'sm:col-span-2' : ''}`}>
      <Label className="text-[12px] text-[#0E1F1A]/65">{label}</Label>
      {children}
    </div>
  );
}
